import { useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  Button,
  FormControl,
  Input,
  ModalBody,
  ModalHeader,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { CrossIcon } from 'components/icons/CrossIcon';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useCollections } from 'hooks/useCollections';
import { useRefetchProjects } from 'state/refetchProjects';

type AddCollectionNameProps = {
  isOpen: boolean;
  onClose(): void;
  onSuccess(arg0: number, arg1: string): void;
};

type NameForm = {
  collectionName: string;
};

export const AddCollectionName = ({
  isOpen,
  onClose,
  onSuccess,
}: AddCollectionNameProps) => {
  const { register, handleSubmit, reset } = useForm<NameForm>({
    mode: 'onChange',
    defaultValues: {
      collectionName: '',
    },
  });
  const { updateRefetchProjectsStatus } = useRefetchProjects();
  const { invalidateGetCollectionsList, createNewCollection } = useCollections();

  const handleNameSave = ({ collectionName }: NameForm) => {
    createNewCollection.mutate({ name: collectionName });
  };

  useEffect(() => {
    // On successful creation of new collection, refetch projects and close modal
    if (createNewCollection.isSuccess) {
      updateRefetchProjectsStatus((prev) => prev + 1);
      invalidateGetCollectionsList();
      reset();
      if (createNewCollection?.data?.id && createNewCollection?.data?.name) {
        onSuccess(createNewCollection?.data?.id, createNewCollection?.data?.name);
      }
    }
  }, [createNewCollection.isSuccess]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent sx={{ pb: '17px' }}>
        <FlexColumn
          sx={{ height: '100%' }}
          justifyContent="flex-start"
          alignItems="center"
        >
          <ModalHeader sx={{ width: '100%', margin: '0px', padding: '17px 16px 0px' }}>
            <FlexRow alignItems="center" justifyContent="space-between">
              <Text size="lg" sx={{ fontSize: '20px' }} fontWeight={700}>
                Create a new list
              </Text>
              <Box sx={{ cursor: 'pointer' }} onClick={onClose}>
                <CrossIcon stroke="#2C402E" opacity={0.2} />
              </Box>
            </FlexRow>
          </ModalHeader>
          <ModalBody sx={{ width: '100%', padding: '24px 16px' }}>
            <form onSubmit={handleSubmit(handleNameSave)}>
              <FormControl sx={{ width: '100%', marginBottom: '24px' }}>
                <Label
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    ml: '6px',
                  }}
                >
                  Name
                </Label>
                <Input
                  className="groupName"
                  variant="outlined"
                  sx={{
                    bgColor: 'transparent',
                    marginTop: '4px',
                    height: '50px',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderColor: 'brand.dark-green-200',
                  }}
                  {...register('collectionName')}
                />
              </FormControl>
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Button
                  color="secondary"
                  onClick={onClose}
                  sx={{
                    color: 'brand.army-green',
                    mr: '0.8rem',
                    padding: '16px',
                    height: '100%',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  value="submit"
                  sx={{
                    backgroundColor: 'brand.army-green',
                    color: 'brand.white',
                    padding: '16px',
                    height: '100%',
                  }}
                >
                  Save & continue
                </Button>
              </Box>
            </form>
          </ModalBody>
        </FlexColumn>
      </ModalContent>
    </Modal>
  );
};
